import * as React from 'react';
import CustomModal from '../CustomModal/CustomModal';
import CustomInput from '../CustomInput/CustomInput';
import Button from '../Button/Button';
import NotificationSnackbar from '../NotificationSnackbar/NotificationSnackbar';

const DELETE_CONFIRMATION = 'delete';

interface DeleteOrganisationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationName: string;
  chatbotNames: string[];
  onConfirm: () => Promise<void>;
}

function DeleteOrganisationModal({
  open,
  onOpenChange,
  organisationName,
  chatbotNames,
  onConfirm
}: Readonly<DeleteOrganisationModalProps>): React.ReactElement {
  const [confirmText, setConfirmText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showError, setShowError] = React.useState(false);

  const isConfirmValid = confirmText.trim() === DELETE_CONFIRMATION;

  React.useEffect(() => {
    if (!open) {
      setConfirmText('');
      setErrorMessage('');
      setShowError(false);
    }
  }, [open]);

  const handleConfirm = async (): Promise<void> => {
    if (!isConfirmValid || isLoading) return;

    try {
      setIsLoading(true);
      setShowError(false);
      await onConfirm();
      onOpenChange(false);
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to delete organisation');
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CustomModal
        open={open}
        onClose={() => onOpenChange(false)}
        title='Delete organisation'
        maxWidth='md'
      >
        <div className='flex flex-col gap-4'>
          <div className='bg-red-50 border border-red-200 rounded p-3'>
            <p className='text-sm text-red-800'>
              <strong>Warning:</strong> This permanently deletes{' '}
              <strong>{organisationName}</strong> and everything belonging to
              it:
            </p>
            <ul className='mt-2 list-disc pl-5 text-sm text-red-800 space-y-1'>
              <li>All users and their access</li>
              <li>Every chatbot, conversation and message history</li>
              <li>All knowledge bases, documents, URLs and embeddings</li>
              <li>Analytics, and every draft and published version</li>
            </ul>
            {chatbotNames.length > 0 && (
              <>
                <p className='mt-2 text-sm text-red-800'>
                  This will permanently delete {chatbotNames.length} chatbot(s)
                  and all associated data:
                </p>
                <ul className='mt-2 list-disc pl-5 text-sm text-red-800 space-y-1'>
                  {chatbotNames.map((chatbotName) => (
                    <li key={chatbotName}>{chatbotName}</li>
                  ))}
                </ul>
              </>
            )}
            <p className='mt-2 text-sm text-red-800'>
              This action cannot be undone.
            </p>
          </div>
          <div className='mt-2'>
            <CustomInput
              label={`Confirm by typing ${DELETE_CONFIRMATION}`}
              type='text'
              name='deleteConfirmation'
              placeholder={`Type ${DELETE_CONFIRMATION} to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </div>
          <div className='flex justify-end gap-3 mt-6'>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirm}
              loading={isLoading}
              disabled={!isConfirmValid}
            >
              Delete organisation
            </Button>
          </div>
        </div>
      </CustomModal>

      <NotificationSnackbar
        message={errorMessage}
        severity='error'
        open={showError}
        onClose={() => setShowError(false)}
      />
    </>
  );
}

export default DeleteOrganisationModal;
